// Kausora Intelligence System (KIS) - Authentication Provider
// Manages user authentication state using Supabase Auth

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../config/supabaseClient';
import { setSentryUser, setSentryContext } from '../../../config/sentry';
import { getKISRoute } from '../../utils/routing';

const AuthContext = createContext({
  user: null,
  profile: null,
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signInWithOAuth: async () => {},
  signOut: async () => {},
  updateProfile: async () => {},
});

const sessionWarnTimeoutMs = 20000;
const sessionTimeoutMs = 60000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const createProfile = useCallback(async (userId, userMetadata = {}) => {
    try {
      console.log('[AuthProvider] Creating profile for user:', userId);
      console.log('[AuthProvider] User metadata:', userMetadata);

      // Extract LinkedIn or other OAuth metadata
      const profileData = {
        id: userId,
        subscription_tier: 'free',
        monthly_query_limit: 10,
        queries_used_this_month: 0,
      };

      // Extract company_name from OAuth metadata if available
      // Note: LinkedIn basic OAuth doesn't provide company, but we prepare for future or other providers
      if (userMetadata.company_name) {
        profileData.company_name = userMetadata.company_name;
      }

      // Extract industry from OAuth metadata if available
      if (userMetadata.industry) {
        profileData.industry = userMetadata.industry;
      }

      // Extract job_title from OAuth metadata if available
      if (userMetadata.job_title) {
        profileData.job_title = userMetadata.job_title;
      }

      console.log('[AuthProvider] Profile data to insert:', profileData);

      const { data, error } = await supabase
        .from('users_profile')
        .insert(profileData)
        .select()
        .single();

      if (error) {
        console.error('[AuthProvider] Profile creation failed:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      console.log('[AuthProvider] Profile created successfully:', data);
      setProfile(data);
      return data;
    } catch (error) {
      console.error('[AuthProvider] Failed to create profile:', error);
      throw error; // Re-throw to allow caller to handle
    }
  }, []);

  const fetchProfile = useCallback(async (userId, userMetadata = {}) => {
    try {
      console.log('[AuthProvider] Fetching profile for user:', userId);
      console.log('[AuthProvider] Session context: auth.uid() should equal', userId);
      console.log('[AuthProvider] User metadata:', userMetadata);

      // Don't call getSession() here - we already have a valid session from SIGNED_IN event
      // Calling getSession() immediately after session save causes lock contention

      // Fetch profile with timeout to prevent hanging
      const profilePromise = supabase
        .from('users_profile')
        .select('*')
        .eq('id', userId)
        .single();

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timed out after 10 seconds')), 10000)
      );

      const { data, error } = await Promise.race([profilePromise, timeoutPromise]);

      if (error) {
        // Log detailed error information
        console.error('[AuthProvider] Profile fetch error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          userId: userId
        });

        // Profile doesn't exist yet - create it
        if (error.code === 'PGRST116') {
          console.log('[AuthProvider] Profile not found (PGRST116), creating new profile...');
          console.log('[AuthProvider] Creating profile with metadata:', userMetadata);
          try {
            await createProfile(userId, userMetadata);
            console.log('[AuthProvider] Profile created successfully via manual insert');
          } catch (createError) {
            console.error('[AuthProvider] Failed to create profile:', createError);
            // Check if trigger might have created it
            const { data: retryData } = await supabase
              .from('users_profile')
              .select('*')
              .eq('id', userId)
              .single();

            if (retryData) {
              console.log('[AuthProvider] Profile created by trigger, fetch succeeded on retry');
              setProfile(retryData);
            } else {
              console.error('[AuthProvider] Profile creation failed completely');
              throw createError;
            }
          }
        } else if (error.code === 'PGRST301') {
          // RLS policy violation - insufficient privileges
          console.error('[AuthProvider] RLS POLICY VIOLATION');
          console.error('[AuthProvider] auth.uid() may be NULL or session not propagated yet');
          console.error('[AuthProvider] Retrying profile fetch after 500ms...');

          // Wait for session to propagate to RLS context
          await new Promise(resolve => setTimeout(resolve, 500));

          // Retry profile fetch
          const { data: retryData, error: retryError } = await supabase
            .from('users_profile')
            .select('*')
            .eq('id', userId)
            .single();

          if (retryError) {
            console.error('[AuthProvider] Profile fetch retry failed:', retryError);
            throw retryError;
          }

          console.log('[AuthProvider] Profile fetch retry succeeded!');
          setProfile(retryData);
          return retryData;
        } else {
          // Other errors
          console.error('[AuthProvider] Unexpected error fetching profile');
          throw error;
        }
      } else {
        console.log('[AuthProvider] Profile fetched successfully');
        setProfile(data);

        // Set Sentry user context for error tracking
        try {
          const currentUser = (await supabase.auth.getUser()).data.user;
          setSentryUser(currentUser);
          setSentryContext('profile', {
            subscription_tier: data.subscription_tier,
            monthly_query_limit: data.monthly_query_limit,
            queries_used_this_month: data.queries_used_this_month,
            company_name: data.company_name,
          });
        } catch (sentryError) {
          console.error('[AuthProvider] Failed to set Sentry context:', sentryError);
        }

        // Update last_login (don't await, let it run in background)
        supabase
          .from('users_profile')
          .update({ last_login: new Date().toISOString() })
          .eq('id', userId)
          .then(() => console.log('[AuthProvider] Last login updated'))
          .catch(err => console.error('[AuthProvider] Failed to update last login:', err));
      }

      setLoading(false);
    } catch (error) {
      console.error('[AuthProvider] Failed to fetch profile:', error);
      setLoading(false);
    }
  }, [createProfile]);

  useEffect(() => {
    let isMounted = true; // Track component mount status
    let warnTimeoutId = null;
    let hardTimeoutId = null;
    let initializingRef = false; // Prevent duplicate initialization (React StrictMode)

    // Helper: Set loading to false safely
    const safeSetLoading = (value) => {
      if (isMounted) {
        setLoading(value);
      }
    };

    const clearTimeouts = () => {
      if (warnTimeoutId) {
        clearTimeout(warnTimeoutId);
        warnTimeoutId = null;
      }
      if (hardTimeoutId) {
        clearTimeout(hardTimeoutId);
        hardTimeoutId = null;
      }
    };

    // Check active session on mount with timeout and error handling
    const initializeAuth = async () => {
      // Prevent duplicate initialization (React StrictMode double-mounting)
      if (initializingRef) {
        console.log('[AuthProvider] Already initializing, skipping duplicate call');
        return;
      }
      initializingRef = true;

      try {
        console.log('[AuthProvider] Initializing authentication...');

        // Check if we're on OAuth callback page with auth code
        const url = new URL(window.location.href);
        const hasAuthCode = url.searchParams.has('code') || url.hash.includes('access_token');

        if (hasAuthCode) {
          console.log('[AuthProvider] OAuth callback detected with code/token in URL');
          console.log('[AuthProvider] URL params:', Array.from(url.searchParams.entries()));
          console.log('[AuthProvider] URL hash:', url.hash);
          console.log('[AuthProvider] Supabase will automatically exchange the code via detectSessionInUrl');
          console.log('[AuthProvider] Waiting for SIGNED_IN event from onAuthStateChange...');

          // DO NOT call getSession() here - it conflicts with automatic code exchange
          // Supabase's detectSessionInUrl:true will handle the exchange automatically
          // and trigger onAuthStateChange with SIGNED_IN event

          // Set a timeout to catch failures - if no SIGNED_IN event after 30 seconds
          hardTimeoutId = setTimeout(() => {
            if (isMounted && !user && loading) {
              console.error('[AuthProvider] OAuth callback timeout - no SIGNED_IN event received after 30s');
              console.error('[AuthProvider] Checking if session exists despite timeout...');

              // Last resort: check for session manually
              supabase.auth.getSession().then(({ data: { session } }) => {
                if (session) {
                  console.log('[AuthProvider] Session found on timeout check!', session.user.email);
                  console.log('[AuthProvider] SIGNED_IN event may have been missed - manually triggering profile fetch');
                  setUser(session.user);
                  fetchProfile(session.user.id, session.user.user_metadata || {});
                } else {
                  console.error('[AuthProvider] No session found - OAuth code exchange failed');
                  safeSetLoading(false);
                }
              });
            }
          }, 30000);

          // Exit early - onAuthStateChange will handle the SIGNED_IN event
          return;
        }

        // Normal flow: check for existing session (page refresh, direct navigation)
        console.log('[AuthProvider] Checking for existing session...');

        // Get session with increased timeout for slow networks or cold starts
        const sessionPromise = supabase.auth.getSession();
        warnTimeoutId = setTimeout(() => {
          console.warn('[AuthProvider] Session retrieval taking longer than expected...');
        }, sessionWarnTimeoutMs);
        const timeoutPromise = new Promise((_, reject) => {
          hardTimeoutId = setTimeout(
            () => reject(new Error(`Session retrieval timed out after ${sessionTimeoutMs / 1000} seconds`)),
            sessionTimeoutMs
          );
        });

        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]);

        // Clear timeouts once we get a response
        clearTimeouts();

        if (error) {
          console.error('[AuthProvider] Error retrieving session:', error);
          safeSetLoading(false);
          return;
        }

        console.log('[AuthProvider] Session retrieved:', session ? 'authenticated' : 'anonymous');

        if (!isMounted) return; // Component unmounted, abort

        setUser(session?.user ?? null);

        if (session?.user) {
          console.log('[AuthProvider] Fetching profile for user:', session.user.id);

          try {
            await fetchProfile(session.user.id, session.user.user_metadata || {});
            console.log('[AuthProvider] Profile fetch complete');
          } catch (profileError) {
            console.error('[AuthProvider] Profile fetch failed:', profileError);
            // Don't block user from logging in if profile fetch fails
            // They can still use the app, just without profile data
            safeSetLoading(false);
          }
        } else {
          console.log('[AuthProvider] No session found, setting loading to false');
          safeSetLoading(false);
        }

      } catch (error) {
        console.error('[AuthProvider] Fatal error during auth initialization:', error);

        // Clear any stuck state
        if (isMounted) {
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      } finally {
        // Clear timeouts
        clearTimeouts();
        initializingRef = false;
      }
    };

    // Start initialization
    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[AuthProvider] ========================================');
        console.log('[AuthProvider] Auth state changed:', event);
        console.log('[AuthProvider] Session:', session ? {
          userId: session.user.id,
          email: session.user.email,
          provider: session.user.app_metadata?.provider,
          accessToken: session.access_token ? 'present' : 'missing'
        } : 'No session');
        console.log('[AuthProvider] ========================================');

        if (!isMounted) {
          console.log('[AuthProvider] Component unmounted, ignoring auth state change');
          return;
        }

        // Clear any pending timeouts when we get a valid auth event
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('[AuthProvider] SIGNED_IN event received, clearing timeouts');
          clearTimeouts();
        }

        setUser(session?.user ?? null);

        if (session?.user) {
          console.log('[AuthProvider] Session established, fetching profile...');
          try {
            await fetchProfile(session.user.id, session.user.user_metadata || {});
          } catch (error) {
            console.error('[AuthProvider] Profile fetch failed on auth change:', error);
            // Still set loading to false even if profile fetch fails
            safeSetLoading(false);
          }
        } else {
          setProfile(null);
          safeSetLoading(false);
        }
      }
    );

    // Cleanup
    return () => {
      isMounted = false;
      clearTimeouts();
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProfile]); // Intentionally not including 'user' and 'loading' - we check their values at timeout time

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { data, error };
  };

  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });

    // Create profile will be handled automatically by auth state change listener

    return { data, error };
  };

  // OAuth sign in with provider
  const signInWithOAuth = async (provider) => {
    // Use domain-aware routing for OAuth callback
    const callbackPath = getKISRoute('/auth/callback');
    const redirectUrl = `${window.location.origin}${callbackPath}`;

    console.log('[AuthProvider] OAuth redirect URL:', redirectUrl);

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: provider, // 'google', 'azure', 'linkedin_oidc', 'facebook'
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) {
      console.error('OAuth sign-in error:', error);
    }

    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setUser(null);
      setProfile(null);

      // Clear Sentry user context on sign out
      try {
        setSentryUser(null);
      } catch (sentryError) {
        console.error('Failed to clear Sentry context:', sentryError);
      }
    }
    return { error };
  };

  const updateProfile = async (updates) => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    const { data, error } = await supabase
      .from('users_profile')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();

    if (!error && data) {
      setProfile(data);
    }

    return { data, error };
  };

  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signInWithOAuth,
    signOut,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
